import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPushPreference, setPushPreference } from "../redux/slices/notificationsSlice";
import usePushEnable from "../hooks/usePushEnable";

// NOTIFY-F007/F008 (docs/TRD/task-notifications-contract.md §6, source TRD §3.8, AC-F6.2,
// AC-F6.6, AC-F6.7, AC-F6.8, AC-F10.3, AC-F10.4, AC-F10.5, R1, R11). Rendered inside
// NotificationBell's dropdown panel — it only ever mounts while the panel is open, which is
// what "on open" means below.
//
// PUSH-NUDGE-001 — the "ask + subscribe" mechanics now live in the shared
// `usePushEnable` hook (src/hooks/usePushEnable.js), reused by the post-login
// PushNudgeBanner. Everything specific to this dropdown control (the on/off *preference*
// toggle, its own strings/markup) stays here unchanged.
const PushSettings = () => {
  const dispatch = useDispatch();
  const pushEnabled = useSelector((state) => state.notifications.pushEnabled);

  const {
    supported,
    pushConfigured,
    pushPermission,
    subscribed,
    subscribing,
    error,
    enable: handleEnable,
  } = usePushEnable();

  // NOTIFY-F008 (AC-F10.3) — the on/off preference only matters once there's a real
  // subscription to toggle, so it's fetched once the 'granted'+subscribed branch mounts
  // (i.e. once per dropdown open where push is already on), not on every render of it. The
  // ref guards against re-fetching if this effect re-runs for an unrelated dependency
  // change while `isOn` stays true.
  const preferenceFetchedRef = useRef(false);
  const isOn = pushPermission === "granted" && subscribed;
  useEffect(() => {
    if (isOn && !preferenceFetchedRef.current) {
      preferenceFetchedRef.current = true;
      dispatch(fetchPushPreference());
    }
  }, [dispatch, isOn]);

  // NOTIFY-F008 (AC-F10.4). Deliberately does NOT touch the browser subscription
  // (`unsubscribe()`) or call the server's `DELETE /push_subscription` — turning the
  // preference off only tells the backend to stop sending; the subscription stays intact so
  // re-enabling resumes delivery with no permission re-prompt.
  const handleTogglePreference = useCallback(
    (event) => {
      dispatch(setPushPreference(event.target.checked));
    },
    [dispatch]
  );

  // AC-F6.8 / R1 — no `serviceWorker`/`PushManager` (the iOS Safari tab case): render no
  // push affordance at all. AC-F6.6 / contract §6 — anything other than a confirmed
  // `enabled: true` from the public-key endpoint (`false`, or still `null`/unresolved):
  // hide the control entirely rather than show-and-break. Review fix (L5) — the strict
  // `!== true` (not `!pushConfigured`) is deliberate: `pushConfigured` starts `null`
  // (unknown, see the slice), and treating "unknown" the same as "known disabled" is what
  // stops the Enable button flashing before the fetch resolves. This gate is
  // `pushConfigured`, not `pushEnabled` (the user's own preference) — the preference
  // toggle below must stay visible even while the user has turned their own preference
  // off (AC-F10.4).
  if (!supported || pushConfigured !== true) {
    return null;
  }

  // Review fix (L5) — `pushPermission` also starts `null` (unknown) until the sync effect
  // above reads the live `Notification.permission`; rendering nothing until it resolves
  // stops the Enable button (or any other branch below) from flashing on that first paint.
  if (pushPermission === null) {
    return null;
  }

  if (pushPermission === "denied") {
    // AC-F6.7 / R11 — persistent, non-nagging, never re-prompts.
    return (
      <div className="wt-push-settings wt-push-settings--denied">
        <p className="wt-push-note">
          Push is off — you&apos;ll still see notifications here. To turn it back on,
          allow notifications for this site in your browser&apos;s site settings.
        </p>
      </div>
    );
  }

  if (isOn) {
    return (
      <div className="wt-push-settings wt-push-settings--on">
        <p className="wt-push-note">Push is on.</p>
        <label className="wt-push-toggle">
          <input
            type="checkbox"
            role="switch"
            aria-checked={pushEnabled}
            aria-label="Push notifications"
            checked={pushEnabled}
            onChange={handleTogglePreference}
          />
          <span>Push notifications</span>
        </label>
        {/* G3 — the in-app bell is never opt-out-able; only push delivery is. */}
        <p className="wt-push-note wt-push-note--muted">
          The in-app bell can&apos;t be turned off.
        </p>
      </div>
    );
  }

  // 'default', or 'granted' without a confirmed subscription yet (e.g. a prior subscribe
  // attempt failed) — offer the same explicit action either way.
  return (
    <div className="wt-push-settings">
      <p className="wt-push-explainer">
        Get notified the moment a timesheet needs your attention, even when this tab
        isn&apos;t open.
      </p>
      <button
        type="button"
        className="wt-push-enable"
        onClick={handleEnable}
        disabled={subscribing}
      >
        {subscribing ? "Enabling…" : "Enable push notifications"}
      </button>
      {error && (
        <p className="wt-push-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default PushSettings;
